import { z } from "@eisox/zod";

import { m } from "~/paraglide/messages";

export interface Group {
  name: string;
  items: string[];
}

type BuildGroupsUpdateRequestBody = {
  itemIds: string[];
  groupNames: string[];
}[];

type BuildGroupsUniqueUpdateRequestBody = {
  itemIds: string[];
  group: string | undefined;
}[];

interface UpdateEntryWithItems {
  itemIds: string[];
}

const addItemToGroupedRequest = <TEntry extends UpdateEntryWithItems, Key>(
  key: Key,
  cache: Map<Key, TEntry>,
  updateRequestBody: TEntry[],
  createEntry: () => TEntry,
  itemId: string,
) => {
  let updateGroup = cache.get(key);
  if (!updateGroup) {
    updateGroup = createEntry();
    cache.set(key, updateGroup);
    updateRequestBody.push(updateGroup);
  }
  updateGroup.itemIds.push(itemId);
};

const collectUngroupedItems = (groups: Group[], initialState: Group[]): string[] => {
  const currentItems = new Set(groups.flatMap(group => group.items));
  const initialItems = new Set(initialState.flatMap(group => group.items));
  return [...initialItems].filter(itemId => !currentItems.has(itemId));
};

export const buildGroupsUpdateRequestBody = (groups: Group[], initialState: Group[]): BuildGroupsUpdateRequestBody => {
  const itemToGroupsMap = new Map<string, string[]>();
  groups.forEach(group => {
    group.items.forEach(itemId => {
      if (itemToGroupsMap.has(itemId)) {
        const existing = itemToGroupsMap.get(itemId);
        if (existing) {
          itemToGroupsMap.set(itemId, [...existing, group.name]);
        }
      } else {
        itemToGroupsMap.set(itemId, [group.name]);
      }
    });
  });

  const updateRequestBody: BuildGroupsUpdateRequestBody = [];
  const groupNamesMap = new Map<string, BuildGroupsUpdateRequestBody[number]>();

  itemToGroupsMap.forEach((groupNames, itemId) => {
    const sortedGroupNames = [...groupNames].sort();
    const groupNameKey = sortedGroupNames.join("-");
    addItemToGroupedRequest(
      groupNameKey,
      groupNamesMap,
      updateRequestBody,
      () => ({ itemIds: [], groupNames: sortedGroupNames }),
      itemId,
    );
  });

  // Items no longer in any group
  const ungroupedItems = collectUngroupedItems(groups, initialState);
  if (ungroupedItems.length > 0) {
    updateRequestBody.push({ groupNames: [], itemIds: ungroupedItems });
  }

  return updateRequestBody;
};

export const buildGroupsUniqueUpdateRequestBody = (
  groups: Group[],
  initialState: Group[],
): BuildGroupsUniqueUpdateRequestBody => {
  const itemToGroupsMap = new Map<string, string>();
  groups.forEach(group => {
    group.items.forEach(itemId => {
      itemToGroupsMap.set(itemId, group.name);
    });
  });

  const updateRequestBody: BuildGroupsUniqueUpdateRequestBody = [];
  const groupNamesMap = new Map<string, BuildGroupsUniqueUpdateRequestBody[number]>();

  itemToGroupsMap.forEach((group, itemId) => {
    addItemToGroupedRequest(group, groupNamesMap, updateRequestBody, () => ({ itemIds: [], group }), itemId);
  });

  // Items no longer in any group
  const ungroupedItems = collectUngroupedItems(groups, initialState);
  if (ungroupedItems.length > 0) {
    updateRequestBody.push({ group: undefined, itemIds: ungroupedItems });
  }

  return updateRequestBody;
};

export const groupsSchema = () =>
  z.object({
    groups: z
      .array(
        z.object({
          name: z.string(),
          items: z.array(z.string()).min(1, m.each_group_must_have_at_least_one_item()),
        }),
      )
      .superRefine((groups, ctx) => {
        const namesSet = new Set();
        for (const group of groups) {
          if (namesSet.has(group.name)) {
            ctx.addIssue({
              code: "custom",
              message: m.unique_group_names(),
            });
          }
          namesSet.add(group.name);
        }
      }),
  });

export type GroupsSchemaType = z.infer<ReturnType<typeof groupsSchema>>;
